import { createSelector } from "reselect";

import { INTEGRATION_STATUS } from "app/pages/sales-intelligence/pages/management/constants/constants";
import type { IntegrationStatusResponse } from "app/pages/sales-intelligence/sub-modules/management/components/types";
import { createStatePropertySelector } from "app/pages/workspace/sales/helpers";

import { calculateCrmListStatus } from "../../utils/crmList";
import { getActiveImportSettings } from "../../utils/utils";

import { selectManagementReducer } from "./managementSelectors";

const selectSalesforceReducer = createStatePropertySelector(selectManagementReducer("salesforceIntegration"));

const selectSalesforceStatusReducer = createStatePropertySelector(selectSalesforceReducer("crmConnectionStatus"));

const selectIsCurrentUserVerifiedBySfCrm = selectSalesforceReducer("isCurrentUserVerifiedByCrm");

export const selectIsCompanyAsLeadExportClaimEnabled = selectSalesforceReducer("companyAsLeadExportClaimEnabled");

export const selectIsSfPredefinedOnCreateUpdateClaimEnabled = selectSalesforceReducer(
    "predefinedOnCreateUpdateClaimEnabled",
);

const selectSalesforceCrmStateInfo = selectSalesforceReducer("crmStateInfo");

export const selectSalesforceAccountsCount = createSelector(
    selectSalesforceCrmStateInfo,
    (crmStateInfo) => crmStateInfo.accountExportFieldsCount,
);

export const selectSalesforceContactsCount = createSelector(
    selectSalesforceCrmStateInfo,
    (crmStateInfo) => crmStateInfo.contactExportFieldsCount,
);

export const selectSalesforceLeadsCount = createSelector(
    selectSalesforceCrmStateInfo,
    (crmStateInfo) => crmStateInfo.leadExportFieldsCount,
);

const selectSfCrmExportLimit = selectSalesforceReducer("crmExportLimit");

export const selectSfCrmExportAccountsLimit = createSelector(selectSfCrmExportLimit, (crmExportLimit) => {
    return crmExportLimit?.accountsLimit ?? Infinity;
});

export const selectSfCrmExportContactsLimit = createSelector(selectSfCrmExportLimit, (crmExportLimit) => {
    return crmExportLimit?.contactsLimit ?? Infinity;
});

export const selectSfCrmExportLeadsLimit = createSelector(selectSfCrmExportLimit, (crmExportLimit) => {
    return crmExportLimit?.leadsLimit ?? Infinity;
});

export const selectSalesforceStatusObject = selectSalesforceReducer("crmConnectionStatus");

export const selectSalesforceAccountsToggleState = createSelector(
    selectSalesforceStatusObject,
    (status) => status?.isExportEnabled,
);

export const selectSalesforceContactsToggleState = createSelector(
    selectSalesforceStatusObject,
    (status) => status?.isContactExportEnabled,
);

export const selectSalesforceLeadsToggleState = createSelector(
    selectSalesforceStatusObject,
    (status) => status?.isLeadsExportEnabled,
);

export const selectSalesforceCompanyAsLeadsToggleState = createSelector(
    selectSalesforceStatusObject,
    (status) => status?.isCompanyAsLeadExportEnabled,
);

const selectExcludeStatusFields = selectSalesforceReducer("excludeStatusFields");

export const selectSfExcludeStatusFields = createSelector(selectExcludeStatusFields, (excludeFields) =>
    excludeFields ? excludeFields : [],
);

export const selectSfExcludeAccountFields = selectSalesforceReducer("excludeAccountFields");

export const selectSfIsLoadingExcludeAccountFields = selectSalesforceReducer("isLoadingExcludeAccountFields");

// Status fields
export const selectSalesforceStatus = selectSalesforceStatusReducer("syncStatus");
export const selectSalesforceSyncFailedReasonCode = selectSalesforceStatusReducer("syncFailedReasonCode");
export const selectSalesforceLastSynced = selectSalesforceStatusReducer("lastSynced");
export const selectIsSalesforceLeadsFilterEnabled = selectSalesforceStatusReducer("isLeadsFilterEnabled");
export const selectIsSalesforceExportEnabled = selectSalesforceStatusReducer("isExportEnabled");
export const selectIsSalesforceCompanyAsLeadExportEnabled =
    selectSalesforceStatusReducer("isCompanyAsLeadExportEnabled");

export const selectIsSfCrmListEnabled = selectSalesforceStatusReducer("isCrmListEnabled");
export const selectSfListObjectsType = selectSalesforceStatusReducer("crmListObjectsType");

export const selectIsSfCrmLabelEnabled = selectSalesforceStatusReducer("isCrmLabelsEnabled");
export const selectSalesforceFeedToggleState = selectSalesforceStatusReducer("isCrmFeedEnabled");
export const selectSalesforceFeedLevel = selectSalesforceStatusReducer("crmFeedLevel");

// States
export const selectSalesforceLoadingState = selectSalesforceReducer("isLoading");

export const selectSfIsLoadingExcludeStatusFields = selectSalesforceReducer("isLoadingExcludeStatusFields");

const selectSfMappingFields = selectSalesforceReducer("mappingFields");
export const selectIsUpdatingSfFilterFields = selectSalesforceReducer("isUpdatingFilterFields");
export const selectIsUpdatingSfStatusField = selectSalesforceReducer("isUpdatingStatusField");

export const selectSfIntegrationLeadFilterStatus = selectSalesforceReducer("isExclusionOn");

export const selectIsSalesForceNotIntegrated = createSelector(
    selectSalesforceStatus,
    (status) => status === INTEGRATION_STATUS.NotIntegrated,
);

export const selectSfCurrentStatusField = createSelector(selectSfMappingFields, (mappingFields) => {
    if (mappingFields) {
        const filteredFields = mappingFields.filter((field) => {
            if (field.metadata === "Type") {
                return true;
            }
            return false;
        });
        return filteredFields.length ? filteredFields[0].externalField : "";
    }
});

export const selectSfFilterFieldNames = createSelector(selectSfMappingFields, (mappingFields) => {
    if (!mappingFields) {
        return [];
    }
    return mappingFields.filter((field) => field.metadata === "AccountListType").map((field) => field.externalField);
});

export const selectActiveSfImportSettings = createSelector(
    selectIsSfCrmListEnabled,
    selectIsSalesforceLeadsFilterEnabled,
    selectIsSfCrmLabelEnabled,
    getActiveImportSettings,
);

export const selectSfIntegrationCrmStatus = createSelector(
    selectSalesforceStatus,
    selectIsSfCrmListEnabled,
    selectIsCurrentUserVerifiedBySfCrm,
    calculateCrmListStatus,
);

export const selectSalesforceStatusResponse = createSelector(
    selectSalesforceStatusObject,
    selectSalesforceCrmStateInfo,
    selectSfCrmExportLimit,
    selectIsCurrentUserVerifiedBySfCrm,
    selectIsCompanyAsLeadExportClaimEnabled,
    selectIsSfPredefinedOnCreateUpdateClaimEnabled,
    (
        crmConnectionStatus,
        crmStateInfo,
        crmExportLimit,
        isCurrentUserVerifiedByCrm,
        companyAsLeadExportClaimEnabled,
        predefinedOnCreateUpdateClaimEnabled,
    ): IntegrationStatusResponse => ({
        crmConnectionStatus,
        crmStateInfo,
        crmExportLimit: crmExportLimit ?? undefined,
        isCurrentUserVerifiedByCrm,
        companyAsLeadExportClaimEnabled,
        predefinedOnCreateUpdateClaimEnabled,
    }),
);
